import React, { FC, memo, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { PaymentsStatisticsTotal } from 'entities/Warehouse';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

interface PaymentStatisticsCardProps {
  title: string;
  data: PaymentsStatisticsTotal[];
  isDiff?: boolean;
}

export const PaymentStatisticsCard: FC<PaymentStatisticsCardProps> = memo((props) => {
  const { title, data, isDiff = false } = props;
  const { t } = useAppTranslation('common');
  const currencySymbol = useGetCurrencySymbol();

  const getValueColor = useCallback(
    (value: number): string => {
      if (isDiff) {
        if (value > 0) return 'text-green-600';
        if (value < 0) return 'text-red-600';
      }
      return 'text-primary';
    },
    [isDiff],
  );

  const totalNetAmount = data.reduce((sum, item) => sum + (item.amount - item.vatAmount), 0);
  const totalVatAmount = data.reduce((sum, item) => sum + item.vatAmount, 0);
  const totalAmount = data.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="font-semibold text-xl mb-4">{title}</div>

      <table className="w-full">
        <thead>
          <tr className="border-b border-secondaryAccent">
            <th className="text-left text-sm text-primaryLight font-medium py-2.5 pr-1 max-w-[250px]">{t('Item Type')}</th>
            <th className="text-right text-sm text-primaryLight font-medium py-2.5 px-1 whitespace-nowrap">{t('Net Amount')}</th>
            <th className="text-right text-sm text-primaryLight font-medium py-2.5 px-1 whitespace-nowrap">{t('Vat amount')}</th>
            <th className="text-right text-sm text-primaryLight font-medium py-2.5 pl-1 whitespace-nowrap">{t('Total amount')}</th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item) => {
              const netAmount = item.amount - item.vatAmount;

              return (
                <tr key={item.type} className="border-b border-secondaryAccent/60 last:border-b-0">
                  <td className="text-base text-primary py-2.5 pr-1 break-words max-w-[250px]">{t(item.type)}</td>
                  <td className={`text-base text-right py-2.5 px-1 whitespace-nowrap font-semibold ${getValueColor(netAmount)}`}>
                    {roundNumber(netAmount)} {currencySymbol}
                  </td>
                  <td className={`text-base text-right py-2.5 px-1 whitespace-nowrap font-semibold ${getValueColor(item.vatAmount)}`}>
                    {roundNumber(item.vatAmount)} {currencySymbol}
                  </td>
                  <td className={`text-base text-right py-2.5 pl-1 whitespace-nowrap font-semibold ${getValueColor(item.amount)}`}>
                    {roundNumber(item.amount)} {currencySymbol}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td className="text-primaryLight text-center py-5 text-base" colSpan={4}>
                {t('No data')}
              </td>
            </tr>
          )}
        </tbody>
        {data.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-secondaryAccent">
              <td className="text-base text-primary font-semibold py-2.5 pr-1">{t('Total')}</td>
              <td className={`text-base text-right py-2.5 px-1 whitespace-nowrap font-semibold ${getValueColor(totalNetAmount)}`}>
                {roundNumber(totalNetAmount)} {currencySymbol}
              </td>
              <td className={`text-base text-right py-2.5 px-1 whitespace-nowrap font-semibold ${getValueColor(totalVatAmount)}`}>
                {roundNumber(totalVatAmount)} {currencySymbol}
              </td>
              <td className={`text-base text-right py-2.5 pl-1 whitespace-nowrap font-bold ${getValueColor(totalAmount)}`}>
                {roundNumber(totalAmount)} {currencySymbol}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
});
